'use client'
import { ActionIcon, Box, Button, Flex, FocusTrapInitialFocus, Modal, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import CloseIcon from '@/assets/icons/close-small.svg'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { MarketPair } from '@/types/resource'
import { formatMarketCap, formatPercentage } from '@/utils/number/format'

import styles from './style.module.css'

// Extended MarketPair type with formatted_amount
type MarketPairWithFormattedAmount = MarketPair & {
  formatted_amount: string
}

const CRYPTO_COM_EXCHANGE_NAME = 'Crypto.com Exchange'

export default function MarketModal({
  opened,
  onClose,
  data,
}: {
  opened: boolean
  onClose: () => void
  data: MarketPair[]
}) {
  const { coin } = useCoinContext()
  const aboutMarketModalT = useTranslations('about.marketModal')
  const t = useTranslations()

  const { amounts } = useCurrencyAmounts({
    amounts: (data || []).map(item => ({
      value: item?.quote_usd_price,
      currencyFormatOptions: {
        symbolOrCode: true,
        showSymbol: true,
        showCode: true,
      },
    })),
    tokenSymbol: coin?.symbol,
    isUsedForTokenPrice: true,
    isExchangeRateBasedOnSymbolCurrency: false,
  })

  if (!data) return null

  // Add formatted_amount to each data item and sort to put Crypto.com Exchange first
  const dataWithFormattedAmount: MarketPairWithFormattedAmount[] = data
    .map((item, index) => ({
      ...item,
      formatted_amount: amounts[index]?.formattedValue || '$0.00',
    }))
    .sort((a, b) => {
      // Put "Crypto.com Exchange" at the top
      if (a.exchange_name === CRYPTO_COM_EXCHANGE_NAME) return -1
      if (b.exchange_name === CRYPTO_COM_EXCHANGE_NAME) return 1
      return 0
    })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      size={480}
      padding={0}
      classNames={{ content: styles.marketModalContent }}
    >
      <FocusTrapInitialFocus />
      {/* Header */}
      <Flex align="center" justify="space-between" px={24} pt={20} pb={12}>
        <Text variant="heading2" c="var(--text-primary)">
          {aboutMarketModalT('title')}
        </Text>
        <ActionIcon
          variant="subtle"
          onClick={onClose}
          size={32}
          style={{ borderRadius: '50%', background: '#141821' }}
        >
          <CloseIcon width={20} height={20} fill="var(--text-secondary)" />
        </ActionIcon>
      </Flex>
      {/* Table Head */}
      <Flex px={24} py={8} style={{ borderBottom: '1px solid var(--border-surface-default)' }}>
        <Box w="33%" style={{ textAlign: 'center', display: 'flex', alignItems: 'center' }}>
          <Text c="var(--text-secondary)" fw={500} variant="body-number">
            {aboutMarketModalT('table.volumePercent')}
          </Text>
        </Box>
        <Box w="33%">
          <Text c="var(--text-secondary)" fw={500} variant="body-number">
            {aboutMarketModalT('table.exchange')}
          </Text>
          <Text c="var(--text-secondary)" fz={12} fw={500}>
            {aboutMarketModalT('table.pair')}
          </Text>
        </Box>
        <Box w="33%" style={{ textAlign: 'right' }}>
          <Text c="var(--text-secondary)" fw={500} variant="body-number">
            {aboutMarketModalT('table.price')}
          </Text>
          <Text c="var(--text-secondary)" fz={12} fw={500}>
            {t('coinTable.headers.24hVolume')}
          </Text>
        </Box>
      </Flex>
      {/* Table Body */}
      <Box>
        {dataWithFormattedAmount.map((row, idx) => (
          <Flex
            key={idx}
            px={24}
            py={16}
            gap={8}
            align="center"
            style={{
              borderBottom:
                idx !== dataWithFormattedAmount.length - 1
                  ? '1px solid var(--border-surface-default)'
                  : 'none',
            }}
          >
            <Flex flex={1}>
              <Text c="var(--text-primary)" variant="body-number">
                {formatPercentage((row.depth_usd_negative_two / row.depth_usd_positive_two) * 100)}%
              </Text>
            </Flex>
            <Flex flex={1} direction="column" gap={4}>
              <Text
                c="var(--text-primary)"
                variant="body-number"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {row.exchange_name}
              </Text>
              <Text c="var(--text-secondary)" fz={12} fw={500}>
                {row.market_pair_name}
              </Text>
            </Flex>
            <Flex flex={1} direction="column" gap={4} style={{ textAlign: 'right' }}>
              <Text c="var(--text-primary)" variant="body-number">
                {row.formatted_amount}
              </Text>
              <Text c="var(--text-secondary)" fz={12} fw={500}>
                ${formatMarketCap(row.quote_usd_volume_24h)}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Box>
      {/* Footer */}
      <Flex justify="center" px={24} py={12}>
        <Button className={styles.marketModalFooter} onClick={onClose}>
          {aboutMarketModalT('close')}
        </Button>
      </Flex>
    </Modal>
  )
}
